import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { checkAuthStatus } from '../../utils/authUtils';
import { Button } from "../widgets/button";

// Internal Banner Component
interface WelcomeAction {
    text: string;
    link?: string;
    onClick?: () => void;
}

interface BannerProps {
    icon?: string
    title: React.ReactNode
    description: string
    primaryAction: WelcomeAction
    secondaryAction?: WelcomeAction
    mobileVariant?: 'card' | 'fullscreen' // default: 'card'
}

function Banner({
    icon,
    title,
    description,
    primaryAction,
    secondaryAction,
    mobileVariant = 'card',
}: BannerProps) {
    const renderButton = (action: WelcomeAction, variant: 'primary' | 'secondary') => {
        const btn = (
            <Button
                variant={variant}
                onClick={action.onClick}
                className="px-6 shadow-sm"
            >
                {action.text}
            </Button>
        );

        if (action.link) {
            return <Link to={action.link}>{btn}</Link>;
        }
        return btn;
    };

    const content = (
        <div className="flex flex-col items-center justify-center h-full">
            {icon && (
                <div className="mb-10 lg:mb-12">
                    <img src={icon} alt="Icon" className="w-36 h-30 lg:w-48 lg:h-40" />
                </div>
            )}

            <h1 className="text-2xl lg:text-4xl font-bold text-center mb-6 text-b leading-tight lg:leading-tight">
                {title}
            </h1>

            <p className="text-base lg:text-lg text-b4 text-left mb-12 lg:mb-20 px-4 max-w-lg lg:max-w-2xl">
                {description}
            </p>

            <div className="flex items-center gap-4">
                {secondaryAction && renderButton(secondaryAction, 'secondary')}
                {renderButton(primaryAction, 'primary')}
            </div>
        </div>
    )

    return (
        <div className="flex items-center justify-center w-full h-full">
            <div className="lg:hidden w-full h-full">
                <div className={`bg-w rounded-2xl p-10 shadow-sm ${mobileVariant === 'card'
                    ? ''
                    : 'h-full flex flex-col justify-center'
                    }`}>
                    {content}
                </div>
            </div>

            <div className="hidden lg:block">
                {content}
            </div>
        </div>
    )
}

interface WelcomeProps {
    // Banner Props
    title: React.ReactNode;
    description: string;
    icon?: string;
    primaryAction: WelcomeAction;
    secondaryAction?: WelcomeAction;

    // Mobile Directory Slot (Optional)
    mobileNavContent?: React.ReactNode;

    // Auth Config
    authMode?: 'always' | 'guest_only'; // 'always': show regardless; 'guest_only': redirect if logged in
    redirectPath?: string;
}

export const PageWelcome: React.FC<WelcomeProps> = ({
    title,
    description,
    icon,
    primaryAction,
    secondaryAction,
    mobileNavContent,
    authMode = 'always',
    redirectPath,
}) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [isAuthChecking, setIsAuthChecking] = useState<boolean>(true);

    useEffect(() => {
        const checkUserAuth = async () => {
            const isAuth = await checkAuthStatus();
            setIsAuthenticated(isAuth);
            setIsAuthChecking(false);
        };
        checkUserAuth();

        const handleAuthChange = (e: Event) => {
            const customEvent = e as CustomEvent;
            if (customEvent.detail?.isAuthenticated !== undefined) {
                setIsAuthenticated(customEvent.detail.isAuthenticated);
            } else {
                checkUserAuth();
            }
        };
        window.addEventListener('authStateChange', handleAuthChange);
        return () => window.removeEventListener('authStateChange', handleAuthChange);
    }, []);

    if (isAuthChecking) {
        return null;
    }

    if (authMode === 'guest_only' && isAuthenticated && redirectPath) {
        return <Navigate to={redirectPath} replace />;
    }

    const isFullScreenMobile = !mobileNavContent;

    return (
        <div className={`h-full bg-bgb lg:p-0 lg:bg-transparent ${isFullScreenMobile ? 'space-y-0' : 'space-y-3'}`}>
            <div className={`flex items-center justify-center ${isFullScreenMobile ? 'h-full p-4' : 'h-auto lg:h-full p-4'}`}>
                <Banner
                    icon={icon}
                    title={title}
                    description={description}
                    primaryAction={primaryAction}
                    secondaryAction={secondaryAction}
                    mobileVariant={isFullScreenMobile ? 'fullscreen' : 'card'}
                />
            </div>

            {mobileNavContent && (
                <div className="lg:hidden">
                    {mobileNavContent}
                </div>
            )}
        </div>
    );
};
